import { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { Outlet } from "react-router-dom";
import {
  selectCurrentToken,
  setCredentials,
  setAuthReady,
  logout,
  selectAuthReady,
} from "./authSlice";
import { useRefreshMutation } from "../api/authApiSlice";

const DEFAULT_TTL_MS = 15 * 60 * 1000; // match your server token lifetime

export default function AuthProvider() {
  const dispatch = useDispatch();
  const [refresh, { isLoading: isRefreshing }] = useRefreshMutation();
  const authReady = useSelector(selectAuthReady);
  const token = useSelector(selectCurrentToken);
  const refreshTimerRef = useRef(null);
  const initRef = useRef(false);
  const [isLongRunningTask, setIsLongRunningTask] = useState(false);

  const scheduleTokenRefresh = useCallback(
    (accessToken) => {
      if (!accessToken) return;

      const decoded = jwtDecode(accessToken);
      const now = Date.now();
      const expMs = decoded?.exp ? decoded.exp * 1000 : null;
      const assumedExpiry =
        expMs ??
        (decoded?.iat
          ? decoded.iat * 1000 + DEFAULT_TTL_MS
          : now + DEFAULT_TTL_MS);

      const refreshDelay = Math.max(assumedExpiry - now - 60_000, 5_000);

      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

      refreshTimerRef.current = setTimeout(
        async () => {
          try {
            if (isRefreshing) return;
            const { accessToken: newToken } = await refresh().unwrap();
            dispatch(setCredentials({ accessToken: newToken }));
            scheduleTokenRefresh(newToken);
          } catch (err) {
            console.error("Auto token refresh failed", err);
            dispatch(logout());
          }
        },
        isLongRunningTask ? 5_000 : refreshDelay
      ); // Refresh more frequently during long tasks
    },
    [dispatch, refresh, isRefreshing, isLongRunningTask]
  );

  const init = useCallback(async () => {
    if (initRef.current) return;
    initRef.current = true;

    if (token) {
      dispatch(setAuthReady(true));
      scheduleTokenRefresh(token);
      return;
    }

    try {
      const { accessToken } = await refresh().unwrap();
      dispatch(setCredentials({ accessToken }));
      scheduleTokenRefresh(accessToken);
    } catch {
      dispatch(logout());
    } finally {
      dispatch(setAuthReady(true));
    }
  }, [dispatch, refresh, token, scheduleTokenRefresh]);

  useEffect(() => {
    init();

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [init]);

  if (!authReady) {
    return null;
  }

  return <Outlet context={{ setIsLongRunningTask }} />;
}

// export default function AuthProvider() {
//   const dispatch = useDispatch();
//   const [refresh, { isLoading: isRefreshing }] = useRefreshMutation();
//   const authReady = useSelector(selectAuthReady);
//   const token = useSelector(selectCurrentToken);
//   const refreshTimerRef = useRef(null);
//   const initRef = useRef(false); // Ref to ensure init runs only once

//   const scheduleTokenRefresh = useCallback(
//     (accessToken) => {
//       if (!accessToken) return;

//       const decoded = jwtDecode(accessToken);
//       const now = Date.now();
//       const expMs = decoded?.exp ? decoded.exp * 1000 : null;

//       const assumedExpiry =
//         expMs ??
//         (decoded?.iat
//           ? decoded.iat * 1000 + DEFAULT_TTL_MS
//           : now + DEFAULT_TTL_MS);

//       const refreshDelay = Math.max(assumedExpiry - now - 60_000, 5_000);

//       if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

//       refreshTimerRef.current = setTimeout(async () => {
//         try {
//           // Check if another refresh is already in progress
//           if (isRefreshing) return;

//           const { accessToken: newToken } = await refresh().unwrap();
//           dispatch(setCredentials({ accessToken: newToken }));
//           scheduleTokenRefresh(newToken);
//         } catch (err) {
//           console.error("Auto token refresh failed", err);
//           dispatch(logout());
//         }
//       }, refreshDelay);
//     },
//     [dispatch, refresh, isRefreshing]
//   );

//   const init = useCallback(async () => {
//     // Prevent multiple calls to init on re-renders
//     if (initRef.current) return;
//     initRef.current = true;

//     // If we already have a token from a previous session, schedule its refresh
//     if (token) {
//       dispatch(setAuthReady(true));
//       scheduleTokenRefresh(token);
//       return;
//     }

//     // Try to refresh once on mount for a new session
//     try {
//       const { accessToken } = await refresh().unwrap();
//       dispatch(setCredentials({ accessToken }));
//       scheduleTokenRefresh(accessToken);
//     } catch {
//       dispatch(logout());
//     } finally {
//       dispatch(setAuthReady(true));
//     }
//   }, [dispatch, refresh, token, scheduleTokenRefresh]);

//   useEffect(() => {
//     init();

//     return () => {
//       // Clear the timer on component unmount
//       if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
//     };
//   }, [init]);

//   if (!authReady) {
//     // Or return a loading spinner if preferred
//     return null;
//   }
//   return <Outlet />;
// }

// import { useEffect, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { jwtDecode } from "jwt-decode";
// import { Outlet } from "react-router-dom";
// import {
//   selectCurrentToken,
//   setCredentials,
//   setAuthReady,
//   logout,
//   selectAuthReady,
// } from "./authSlice";
// import { useRefreshMutation } from "../api/authApiSlice";

// const DEFAULT_TTL_MS = 15 * 60 * 1000; // match your server token lifetime

// export default function AuthProvider() {
//   const dispatch = useDispatch();
//   const [refresh] = useRefreshMutation();
//   const authReady = useSelector(selectAuthReady);
//   const token = useSelector(selectCurrentToken);
//   const refreshTimerRef = useRef(null);

//   const scheduleTokenRefresh = (accessToken) => {
//     if (!accessToken) return;

//     let decoded = null;
//     decoded = jwtDecode(accessToken);

//     const now = Date.now();
//     const expMs = decoded?.exp ? decoded.exp * 1000 : null;

//     // Fallback: derive from iat if present; else default TTL
//     const assumedExpiry =
//       expMs ??
//       (decoded?.iat
//         ? decoded.iat * 1000 + DEFAULT_TTL_MS
//         : now + DEFAULT_TTL_MS);

//     // refresh 60s before expiry, clamp to at least 5s from now
//     const refreshDelay = Math.max(assumedExpiry - now - 60_000, 5_000);

//     if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

//     refreshTimerRef.current = setTimeout(async () => {
//       try {
//         const { accessToken: newToken } = await refresh().unwrap();
//         dispatch(setCredentials({ accessToken: newToken }));

//         // reschedule for the new token
//         scheduleTokenRefresh(newToken);
//       } catch (err) {
//         console.error("Auto token refresh failed", err);
//         dispatch(logout());
//       }
//     }, refreshDelay);
//   };

//   useEffect(() => {
//     let alive = true;

//     const init = async () => {
//       // If we already have a token, mark ready & schedule
//       if (token) {
//         dispatch(setAuthReady(true));
//         scheduleTokenRefresh(token);
//         return;
//       }

//       // Otherwise try to refresh once on mount
//       try {
//         const { accessToken } = await refresh().unwrap();
//         if (!alive) return;
//         dispatch(setCredentials({ accessToken }));
//       } catch {
//         dispatch(logout());
//       } finally {
//         if (alive) {
//           dispatch(setAuthReady(true));
//           // If we obtained a token above, the selector will update shortly,
//           // but scheduling now is harmless
//           scheduleTokenRefresh(token || null);
//         }
//       }
//     };

//     init();

//     return () => {
//       alive = false;
//       if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
//     };
//   }, [dispatch, refresh, token]);

//   if (!authReady) return null;
//   return <Outlet />;
// }

// import { useEffect, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { jwtDecode } from "jwt-decode";
// import { Outlet } from "react-router-dom";
// import {
//   selectCurrentToken,
//   setCredentials,
//   setAuthReady,
//   logout,
//   selectAuthReady,
// } from "./authSlice";
// import { useRefreshMutation } from "../api/authApiSlice";

// const DEFAULT_TTL_MS = 15 * 60 * 1000; // fallback: 15min

// export default function AuthProvider() {
//   const dispatch = useDispatch();
//   const [refresh] = useRefreshMutation();
//   const authReady = useSelector(selectAuthReady);
//   const token = useSelector(selectCurrentToken);
//   const refreshTimerRef = useRef(null);

//   const scheduleTokenRefresh = (accessToken) => {
//     if (!accessToken) return;

//     let decoded = null;
//     try {
//       decoded = jwtDecode(accessToken);
//     } catch {
//       // if undecodable, fallback to default TTL
//     }

//     const now = Date.now();
//     const expMs = decoded?.exp ? decoded.exp * 1000 : null;

//     // Fallback: assume DEFAULT_TTL if exp missing
//     const assumedExpiry =
//       expMs ??
//       (decoded?.iat
//         ? decoded.iat * 1000 + DEFAULT_TTL_MS
//         : now + DEFAULT_TTL_MS);

//     // refresh 60s before expiry, but minimum 5s from now
//     const refreshDelay = Math.max(assumedExpiry - now - 60_000, 5_000);

//     if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

//     refreshTimerRef.current = setTimeout(async () => {
//       try {
//         const { accessToken: newToken } = await refresh().unwrap();

//         // 👇 this was missing — update Redux state with fresh token
//         dispatch(setCredentials({ accessToken: newToken }));

//         // reschedule with new token
//         scheduleTokenRefresh(newToken);
//       } catch (err) {
//         console.error("Auto token refresh failed", err);
//         dispatch(logout());
//       }
//     }, refreshDelay);
//   };

//   useEffect(() => {
//     let alive = true;

//     const init = async () => {
//       if (token) {
//         dispatch(setAuthReady(true));
//         scheduleTokenRefresh(token);
//         return;
//       }

//       // try one-time refresh on mount
//       try {
//         const { accessToken } = await refresh().unwrap();

//         if (!alive) return;

//         // 👇 put fresh token into Redux
//         dispatch(setCredentials({ accessToken }));
//         dispatch(setAuthReady(true));

//         scheduleTokenRefresh(accessToken);
//       } catch {
//         dispatch(logout());
//         if (alive) dispatch(setAuthReady(true));
//       }
//     };

//     init();

//     return () => {
//       alive = false;
//       if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
//     };
//   }, [dispatch, refresh, token]);

//   if (!authReady) return null;
//   return <Outlet />;
// }
